import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Role, WebRoleService} from 'web-angular-client';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {AlertService} from '../../../service/alert.service';
import {AuthenticationService} from '../../../service/authentication.service';
import {ASecureForm} from '../../../interface/form/ASecureForm';
import {EMPTY, Observable, Subject} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {EnvironmentService} from '../../../service/environment.service';

@Component({
    selector: 'app-role-selector',
    templateUrl: './role-selector.component.html',
    styleUrls: ['./role-selector.component.scss'],
    standalone: false
})
export class RoleSelectorComponent extends ASecureForm implements OnInit
{

    roles: Role[];
    @Input()
    defaultRoleSubject: Subject<Role>;
    @Output()
    outputRole = new EventEmitter<Role>();
    roleForm: UntypedFormGroup;
    defaultRole: Role;

    constructor(
        protected formBuilder: UntypedFormBuilder,
        protected alertService: AlertService,
        protected authenticationService: AuthenticationService,
        protected webRoleService: WebRoleService,
        protected environmentService: EnvironmentService
    )
    {
        super(authenticationService, environmentService);
    }

    ngOnInit()
    {
        this.roleForm = this.formBuilder.group(
            {selectedRole: ['']});

        this.loadRoles().subscribe(() =>
            {
            }
            , (error) =>
            {
                this.alertService.error(error);
            });

        // Check if a default role is provided, if so, set it as the default selected role
        if (this.defaultRoleSubject)
        {
            this.defaultRoleSubject.subscribe(
                (role: Role) =>
                {
                    this.defaultRole = role;
                    // only set the role in the form if the list of supported roles has been loaded
                    // retrieving the list of supported roles is a separate asynchronous operation.
                    if (this.roles)
                    {
                        this.roleForm.controls.selectedRole.setValue(role.description);
                    }
                });
        }
    }

    emitSelectedRole()
    {
        const selectedRole: Role = this.getRoleFromRoleDescription(this.roleForm.controls.selectedRole.value);
        this.outputRole.emit(selectedRole);
    }

    removeGuestAdminRole(roleList: Role[]): Role[]
    {
        if (roleList)
        {
            const index = roleList.findIndex(role => role.name === Role.NameEnum.GUEST_ADMIN);

            if (index > -1)
            {
                roleList.splice(index, 1);
            }
        }

        return roleList;
    }

    protected getRoleUIMeaning(role: Role)
    {
        if (role.name === Role.NameEnum.SUPER_ADMIN)
        {
            return 'Full system functionality including the ability to view and edit account settings.';
        }
        else if (role.name === Role.NameEnum.ADMIN)
        {
            return 'Can view, create, edit, send and cancel request to pay transactions. Can also view request to pay transaction audit' +
                ' trail and the payments dashboard.';
        }
        else if (role.name === Role.NameEnum.FIELD_AGENT)
        {
            return 'Can only create and send request to pay transactions.';
        }
    }

    /**
     * Load a list of supported roles available for selection and set the default role selected.
     * If a default role has been provided, this will be set as the default, otherwise the first
     * role in the list will automatically be set as the default.
     */
    private loadRoles(): Observable<void>
    {
        return this.webRoleService.listRoles().pipe(switchMap((roleList) =>
        {
            this.roles = this.removeGuestAdminRole(roleList);

            if (!this.defaultRole)
            {
                this.defaultRole = this.roles[0];
            }
            this.roleForm.controls.selectedRole.setValue(this.defaultRole.description);
            this.emitSelectedRole();
            return EMPTY;
        }));
    }

    /**
     * @return {@link Role} matching the provided role description. Null otherwise.
     */
    private getRoleFromRoleDescription(roleDescription: string): Role
    {
        let role: Role = null;
        return this.roles.find(r => r.description === roleDescription, role);
    }
}